import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PersistGate } from 'redux-persist/integration/react';
import AppBase from '@atlassian/jira-app-base';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import {
	SpotlightManager,
	type CustomDateRange,
	VIEW_MODES,
} from '@atlassian/jira-portfolio-3-common';
import { context } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics';
import { initialLoad } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command';
import { setInitialFilterValues } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/filters';
import { getFilteredIssues } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues';
import { getTimelineExportWidth } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/section-widths';
import { getViewMode } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/view-settings';
import { setIsExportPreview } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/app/actions';
import {
	CHANGE_OPTION,
	CHANGE_MODE,
	timeScaleCustomDatesChange,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/time-scale/actions';
import store, {
	persistor,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/store';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types';
import { updateColumnWidths } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/section-widths/actions';
import PortfolioErrorBoundary from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-boundary';
import ErrorNotification from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-notification';
import { provideComponents } from '@atlassian/jira-portfolio-3-portfolio/src/common/component-slots';
import type { MapDispatchToPropsSimple } from '@atlassian/jira-portfolio-3-portfolio/src/common/types/redux';
import {
	ERROR_REPORTING_PACKAGE,
	ERROR_REPORTING_TEAM,
	PACKAGE_NAME,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { getNonTimelineWidth } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux';
import AdvancedFieldsMenu from '../component-slot-content/advanced-fields-menu';

import CellSelectWithRollup from '../component-slot-content/cell-select-with-rollup';
import CellTextField from '../component-slot-content/cell-textfield';
import FieldDatePicker from '../component-slot-content/field-date-picker';
import FieldMultiSelectCell from '../component-slot-content/field-multi-select-cell';
import FieldPrioritySelect from '../component-slot-content/field-priority-select';
import FieldSelect from '../component-slot-content/field-select';
import FieldUserAvatarCell from '../component-slot-content/field-user-avatar-cell';
import UserPicker from '../component-slot-content/field-user-picker';
import IssueSearchWarningClearFilter from '../component-slot-content/issue-search-warning-clear-filter';
import NowLine from '../component-slot-content/now-line';
import type { OwnProps, StateProps, InitialConfig, DispatchProps } from './types';
import View from './view';

const mapStateToProps = (state: State): StateProps => ({
	appWidth:
		getNonTimelineWidth() +
		(getViewMode(state) === VIEW_MODES.TIMELINE ? getTimelineExportWidth(state) : 0),
	numIssues: getFilteredIssues(state).length,
});

const mapDispatchToProps: MapDispatchToPropsSimple<DispatchProps> = (dispatch) => ({
	initialLoad: (config: InitialConfig) => {
		dispatch(initialLoad(config));
	},
	setInitialFilterValues: () => dispatch(setInitialFilterValues()),
	updateColumnWidths: (
		scopeWidth: number,
		fieldsWidth: number,
		timelineWidth: number,
		timelineExportWidth: number,
	) => {
		dispatch(
			updateColumnWidths({
				scope: scopeWidth,
				fields: fieldsWidth,
				timeline: timelineWidth,
				timelineExport: timelineExportWidth,
			}),
		);
	},
	updateTimelineDateRange: (timelineDateRange: CustomDateRange) => {
		dispatch({
			type: CHANGE_MODE,
			payload: 'custom',
		});
		dispatch({
			type: CHANGE_OPTION,
			payload: 'custom',
		});
		dispatch(timeScaleCustomDatesChange(timelineDateRange));
	},
	setIsExportPreview: (isExportPreview: boolean) => {
		dispatch(setIsExportPreview(isExportPreview));
	},
});

const Connected = connect(mapStateToProps, mapDispatchToProps)(View);

const App = ({ locale }: OwnProps) => {
	useEffect(() => {
		/**
		 * We are adding this special class to 'jira-frontend' div to scope the react virtualized
		 * classes defined in src/app-export-view/styles.css. CSS styles for these classes can be found
		 * in many places and we are scoping them all.
		 * When we are in Jira SPA, we can not pollute the global classes and hence we are
		 * scoping them to ARJ application.
		 * We remove the special .arj-export-view class once our app is unmounted. Look into the returned function below.
		 */
		const JfeRootElement = document.getElementById('jira-frontend');
		if (JfeRootElement) {
			JfeRootElement.classList.add('arj-export-view');
		}

		return function removeArjClass() {
			// Remove special .arj class from 'jira-frontend' div.
			// eslint-disable-next-line @typescript-eslint/no-shadow
			const JfeRootElement = document.getElementById('jira-frontend');
			if (JfeRootElement) {
				JfeRootElement.classList.remove('arj-export-view');
			}
		};
	}, []);

	return (
		<AppBase store={store} hasErrorBoundary={false}>
			<ContextualAnalyticsData
				sourceType={SCREEN}
				sourceName="portfolio3ExportView"
				attributes={{ context: context.PLAN }}
			>
				{/**
                    PortfolioErrorBoundary is portfolio specific error boundary and is used to
                    show fallback UI when React tree is broken and also show error flags for
                    other errors.
                 */}
				<PortfolioErrorBoundary>
					{/**
                        ReportErrors is an error boundary provided by JFP. It reports errors to
                        SignalFX and Sentry. It report error messages and error stacktraces only to
                        Sentry, but not to SignalFX. SignalFX only gets error count.
                        This component also rethrows the error, so that we can show fallback UI.
                     */}
					<ReportErrors
						id={ERROR_REPORTING_PACKAGE.APP}
						packageName={PACKAGE_NAME}
						teamName={ERROR_REPORTING_TEAM}
						sendToPrivacyUnsafeSplunk
					>
						{document.body && createPortal(<ErrorNotification />, document.body)}
						<PersistGate loading={null} persistor={persistor}>
							<SpotlightManager>
								<Connected locale={locale} />
							</SpotlightManager>
						</PersistGate>
					</ReportErrors>
				</PortfolioErrorBoundary>
			</ContextualAnalyticsData>
		</AppBase>
	);
};

export default provideComponents<OwnProps>({
	AdvancedFieldsMenu,
	FieldSelect,
	FieldPrioritySelect,
	FieldMultiSelectCell,
	FieldDatePicker,
	CellSelectWithRollup,
	FieldUserAvatarCell,
	CellTextField,
	IssueSearchWarningClearFilter,
	NowLine,
	UserPicker,
})(App);
